/**
 * Split a document into several.
 *
 * Every mode previews before it commits: splitting a 200-page bylaw the wrong
 * way and getting a ZIP of 200 files is annoying enough that showing the
 * filenames first is worth the extra round trip.
 *
 * The source document is never modified -- a split produces new files.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, type SplitMode, type SplitPreview, type SplitSpec } from '../api'

interface Props {
  open: boolean
  sessionId: string | null
  pageCount: number
  onCancel: () => void
  onDownloaded: (count: number) => void
}

const MODES: { id: SplitMode; label: string; hint: string }[] = [
  { id: 'individual', label: 'Every page', hint: 'One file per page.' },
  { id: 'every', label: 'Fixed size', hint: 'Consecutive chunks of N pages.' },
  { id: 'at', label: 'At page', hint: 'Break before the pages you name.' },
  { id: 'ranges', label: 'Custom ranges', hint: 'One file per range.' },
]

export function SplitDialog({
  open,
  sessionId,
  pageCount,
  onCancel,
  onDownloaded,
}: Props) {
  const [mode, setMode] = useState<SplitMode>('individual')
  const [every, setEvery] = useState(2)
  const [ranges, setRanges] = useState('')
  const [at, setAt] = useState('')
  const [preview, setPreview] = useState<SplitPreview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const spec = useCallback((): SplitSpec => {
    switch (mode) {
      case 'every':
        return { mode, every }
      case 'ranges':
        return { mode, ranges }
      case 'at':
        return {
          mode,
          at: at
            .split(',')
            .map((v) => Number.parseInt(v.trim(), 10))
            .filter((v) => Number.isFinite(v)),
        }
      default:
        return { mode }
    }
  }, [mode, every, ranges, at])

  // Preview follows the form, debounced so typing a range does not spam.
  useEffect(() => {
    if (!open || !sessionId) return
    let cancelled = false
    setError('')

    const timer = window.setTimeout(() => {
      void api
        .splitPreview(sessionId, spec())
        .then((result) => !cancelled && setPreview(result))
        .catch((err) => {
          if (cancelled) return
          setPreview(null)
          setError(err?.message ?? 'That split is not valid.')
        })
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [open, sessionId, spec])

  useEffect(() => {
    if (open) {
      setMode('individual')
      setRanges('')
      setAt('')
      setPreview(null)
      setError('')
    }
  }, [open])

  if (!open) return null

  const download = async () => {
    if (!sessionId || !preview) return
    setBusy(true)
    try {
      const blob = await api.split(sessionId, spec())
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'split.zip'
      anchor.click()
      // Revoking immediately can cancel the download in some builds.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
      onDownloaded(preview.count)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const current = MODES.find((m) => m.id === mode)

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Split document">
      <div className="modal__panel">
        <h2 className="modal__title">Split document &mdash; {pageCount} pages</h2>

        <div className="modal__tabs" role="tablist">
          {MODES.map((m) => (
            <button
              key={m.id}
              role="tab"
              aria-selected={mode === m.id}
              className="modal__tab"
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="modal__body">
          <p className="modal__hint">{current?.hint}</p>

          {mode === 'every' && (
            <label className="field field--inline">
              <span>Pages per file</span>
              <input
                type="number"
                min={1}
                max={Math.max(1, pageCount - 1)}
                value={every}
                onChange={(e) => setEvery(Math.max(1, Number(e.target.value) || 1))}
              />
            </label>
          )}

          {mode === 'at' && (
            <label className="field">
              <span>Break before pages</span>
              <input
                value={at}
                placeholder="e.g. 4, 9"
                onChange={(e) => setAt(e.target.value)}
              />
            </label>
          )}

          {mode === 'ranges' && (
            <label className="field">
              <span>Ranges</span>
              <input
                value={ranges}
                placeholder="e.g. 1-3, 4, 5-"
                onChange={(e) => setRanges(e.target.value)}
              />
            </label>
          )}

          {error && <p className="modal__hint modal__hint--warn">{error}</p>}

          {preview && !error && (
            <div className="split-preview">
              <div className="split-preview__head">
                {preview.count} file{preview.count === 1 ? '' : 's'}
              </div>
              <ul className="split-preview__list">
                {preview.files.slice(0, 40).map((file) => (
                  <li key={file.name}>
                    <code>{file.name}</code>
                    <span>{(file.bytes / 1024).toFixed(0)} KB</span>
                  </li>
                ))}
              </ul>
              {preview.files.length > 40 && (
                <div className="split-preview__more">
                  &hellip; and {preview.files.length - 40} more
                </div>
              )}
            </div>
          )}

          <p className="modal__hint">
            The document you are editing is not modified; the split is delivered
            as a ZIP.
          </p>
        </div>

        <div className="modal__footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={!preview || Boolean(error) || busy}
            onClick={() => void download()}
          >
            {busy ? 'Preparing…' : `Download ${preview?.count ?? 0} files`}
          </button>
        </div>
      </div>
    </div>
  )
}
